import type { CategoryInfo } from "./categoryTypes";
import type { OrderUnit } from "./productOrderTypes";

export interface ProductBasics {
   sku: string;
   name: string;
}
export type CustomerType = "Isbar" | "Dagligvare";
export type AccessoryType = "Saus" | "Strøssel" | "Kjeks" | "Annet tilbehør" | "Softisdeler";
export type categoryReference = { externalReference: string; name: string };

export type ProductAndCategoriesPayload = {
   products: Product[];
   categories: CategoryInfo[];
   metadata: { options: Partial<Record<LegalFilterKey, string[]>> };
};

export interface RawProduct extends ProductBasics {
   weight: number;
   buyableInBaseUnit: boolean;
   baseUnit: OrderUnit;
   buyableInSalesUnit: boolean;
   salesUnit: OrderUnit;
   variableWeight: boolean;
   conversionFactor: number | null;
   comparativeUnit: string | null;
   epdNumber: string | null;
   description?: string | null;
   eans?: Ean[] | null;
   preservable: string | null;
   type?: string | null;
   similarProducts: string[];
   replacementProducts?: string[];
   newUntilDate: string | null;
   expiringFromDate?: string | null;
   brand: string | null;
   brands?: string[] | null;
   certifications: string[] | null;
   countryOfOrigin: string | null;
   searchWord: string[];
   nutritions?: NutritionLineType[] | ProductNutrition[] | null;
   nutritionBaseUnit?: string;
   allergens: string | null;
   ingredients: string | null;
   packaging: string | null;
   storage: string | null;
   recyclingChargeBaseUnit: number;
   objectID: string;
   leadTime: string | null;
   miniTank: boolean;
   url_key: string;
   size?: string | null;
   sizegroup?: string;
   taste?: string | null;
   customertype?: string;
   parentCategory?: categoryReference;
   category?: categoryReference;
   accessorytype?: string;
   expired?: boolean;
   weightrange?: string | null;
   volumerange?: string | null;
   sizerange?: string | null;
   tastes?: string[];
   tastegroups?: string[];
   allergyinfo?: string[];
   format?: string | null;
   manuallabels?: string[];
   manualshortcuts?: string[];
}

export interface DerivedProduct {
   badges: Badge[];
   labels: Label[];
   newSkus?: string[];
   inAgreementAssortment: boolean;
   price: number | null;
   unit: OrderUnit;
   priceBeforeDiscount: number | null;
   comparisonPrice: number | null;
   comparisonUnit: string | null;
   vatRate: number | null;
   hasPromotion?: boolean;
}

export type Product = RawProduct & DerivedProduct;

export type SimplifiedProduct = Pick<Product, "sku" | "name" | "baseUnit" | "unit" | "price" | "conversionFactor">;

export type AssortmentEntry = {
   itemId: string;
   inAgreementAssortment: boolean;
   preferredProduct?: boolean | null;
   dpakPrice: number | null;
   dpakPriceBeforeDiscount: number | null;
   dpakPerUnitPrice?: number | null;
   dpakPerUnitPriceBeforeDiscount?: number | null;
   fpakPrice?: number | null;
   fpakPriceBeforeDiscount?: number | null;
   comparisonPriceUnit: string | null;
   comparisonPrice: number | null;
   promotionalProduct?: boolean;
   canBuy?: boolean;
   VAT?: number;
   allowedOrderLineUnits?: string[];
   hasPromotion?: boolean;
};

export enum PageType {
   ProductListPage = "plp",
   ProductDetailsPage = "pdp"
}

export type LabelText =
   | "Nyhet"
   | "Sesong"
   | "Avtalesortiment"
   | "Utgått"
   | "Veievare"
   | "Siste sjanse"
   | "Økologisk"
   | "Egne frister"
   | string;

export const labelColors = ["red", "blue", "green", "gray", "violet", "yellow"] as const;
export const labelPositions = ["top-left", "top-right", "bottom-left", "bottom-right"] as const;

export const isLabelColor = (color: string): color is LabelColorVariant => {
   return (labelColors as readonly string[]).includes(color);
};

export const isLabelPosition = (position: string): position is LabelPositionVariant => {
   return (labelPositions as readonly string[]).includes(position);
};

export type LabelColorVariant = (typeof labelColors)[number];
export type LabelPositionVariant = (typeof labelPositions)[number];

export type Label = {
   text: LabelText;
   color: LabelColorVariant;
   position: LabelPositionVariant;
   page: PageType[];
   icon?: React.ElementType;
};

export type BadgeKey = "nyt_norge" | "nokkelhullet" | "pant_2kr" | "pant_3kr";

export type Badge = {
   key: BadgeKey;
   name: string;
};

export type Ean = {
   sku: string;
   code: string;
   packaging: string;
};

export type NutritionLineType = {
   name: string;
   value: string;
};

export type ProductNutrition = {
   productName: string;
   productNutritions: NutritionLineType[];
};

export type ProductTileCallbacks = {
   addToCart?: (product: Product, listname: string, position: number) => void;
   click?: (product: Product, listname: string, position: number) => void;
   impression?: (product: Product, listname: string, position: number) => void;
};

export type FlyingProductOrItemAnimationState = {
   animationId: string;
   x: number;
   y: number;
   height: number;
   sourceWidth: number;
   sku?: string;
   landingPadId?: string;
};

// These are the legal product attribute fields that you can build filters for, if you add
// filterAttributes for any of the sites you should also add them here.
export const legalFilterKeys = [
   "brand",
   "brands",
   "format",
   "certifications",
   "size",
   "taste",
   "type",
   "accessorytype",
   "customertype",
   "weightrange",
   "volumerange",
   "sizerange",
   "tastegroups",
   "tastes",
   "allergyinfo",
   "manuallabels",
   "manualshortcuts"
] as const;

export type LegalFilterKey = (typeof legalFilterKeys)[number];

export type FilterSettings = {
   field: LegalFilterKey;
   name: string;
   values: string[];
   radioDefault?: string;
};

export type Filters = {
   [key in LegalFilterKey]?: string[];
};

export type ProductAvailability = {
   productNumber: string;
   plannedAvailableDate: string | null;
   delayedDeliveryDate: string | null;
   availableQuantity: number;
   customerWarehouse: string | null;
   warehouse: string | null;
   available: boolean;
   partialAvailable: boolean;
   supplyingWarehouse: string;
   atCustomersWarehouse: boolean;
};

export type AvailabilityWithRequestedQuantity = ProductAvailability & {
   requestedQuantity: number;
};

export type AvailabilityWithQuantities = AvailabilityWithRequestedQuantity & {
   selectedQuantity: number;
};

export type Unavailable = AvailabilityWithQuantities & {
   available: false;
};

export type ProductAndAvailability = {
   product: Product;
   availability: AvailabilityWithQuantities;
};

export type CartItem = {
   sku: string;
   qty: number;
   unit: OrderUnit;
   fromEditing?: boolean;
};

export type OrderRefs = {
   orderName: string;
   customerReference: string;
   customerOrderNumber: string;
   replacesOrderNumber?: string;
};

export type ExpandedCartItem = CartItem & Pick<Product, "price" | "vatRate">;

export type CartItemDelta = CartItem & {
   qtyDelta: number;
};

export type LostSaleItem = {
   sku: string;
   quantity: number;
   orderLineUnit: OrderUnit;
   isLostSales: true;
   lostSalesWareHouse: string;
};

export type CartTotals = {
   total: number;
   recyclingCharge: number;
};

export function hasKey<O extends object>(obj: O, key: string | number | symbol): key is keyof O {
   return key in obj;
}

export function isFilterAttribute(key: keyof Product): key is LegalFilterKey {
   return legalFilterKeys.includes(key as LegalFilterKey);
}

export type FilterOptionsByField = Partial<Record<LegalFilterKey, string[]>>;

export const automaticShortcutFilters = [
   "nyheter",
   "avtalt_sortiment",
   "okologisk",
   "siste_sjanse",
   "laktosefri",
   "rabatter"
] as const;

type ManualShortcutFilter = string;

export type ShortcutFilter = (typeof automaticShortcutFilters)[number] | ManualShortcutFilter;

export const productResolutionFilters = ["allProducts", "viewable", "buyable", "inAssortment"] as const;

export type ProductResolutionType = (typeof productResolutionFilters)[number];
